// PublicThreadPage.jsx - ENHANCED VERSION WITH BUBBLE LINKS & CONTEXT
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Avatar, Paper, IconButton, Button, Chip, Divider, 
  Skeleton, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Card, CardContent, Collapse, Container, Fab, AppBar, Toolbar,
  SwipeableDrawer, useMediaQuery, Tooltip
} from '@mui/material';
import { useTheme, alpha, keyframes } from '@mui/material/styles';
import {
  ArrowBack as BackIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  EmojiEvents as AchievementIcon,
  VisibilityOff as PrivateIcon,
  Forum as ThreadIcon,
  Favorite as LikeIcon,
  Login as LoginIcon,
  PersonAdd as SignUpIcon,
  ChatBubbleOutline as ReplyIcon,
  Public as GlobalIcon,
  Groups as BubbleIcon,
  TrendingUp as TrendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import API from '../../../../API';
import MediaDisplay from './components/mediaDisplay';
import { styled } from "@mui/material/styles";

// ───────── Constants ─────────
const APPSHELL_HEIGHT = 86;
const BOTTOM_PADDING = 12;

// ───────── Animations ─────────
const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
  }
  50% {
    box-shadow: 0 0 40px rgba(59, 130, 246, 0.6);
  }
`;

// ───────── Styled Components ─────────
const ModernGradientHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  position: "relative",
  overflow: "hidden",
  minHeight: 300,
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    minHeight: 240,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(45deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
    pointerEvents: "none",
    zIndex: 2,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "50%",
    background: "linear-gradient(to top, rgba(0,0,0,0.25) 0%, transparent 100%)",
    pointerEvents: "none",
    zIndex: 2,
  },
}));

const MobileAppBar = styled(AppBar)(({ theme, scrolled }) => ({
  background: scrolled 
    ? theme.palette.background.paper
    : 'transparent',
  boxShadow: scrolled ? theme.shadows[4] : 'none',
  transition: 'all 0.3s ease',
  backdropFilter: scrolled ? 'blur(20px)' : 'none',
}));

const StickyShareButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: APPSHELL_HEIGHT + BOTTOM_PADDING,
  right: theme.spacing(2),
  left: theme.spacing(2),
  borderRadius: 16,
  height: 56,
  width: 'auto',
  maxWidth: 400,
  margin: '0 auto',
  zIndex: 999,
  boxShadow: theme.shadows[12],
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  fontSize: '1.05rem',
  fontWeight: 700,
  textTransform: 'none',
  animation: `${slideUp} 0.4s ease-out`,
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[20],
  },
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const MobileCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: theme.shadows[2],
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  animation: `${scaleIn} 0.4s ease-out`,
}));

const MobileButton = styled(Button)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(1.75, 3),
  fontWeight: 700,
  textTransform: "none",
  fontSize: "1.05rem",
  transition: "all 0.2s ease",
  '&:active': {
    transform: 'scale(0.97)',
  },
}));

const BottomSheet = styled(SwipeableDrawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    maxHeight: '90vh',
    overflow: 'visible',
  },
}));

const BottomSheetHandle = styled(Box)(({ theme }) => ({
  width: 40,
  height: 5,
  borderRadius: 3,
  backgroundColor: theme.palette.divider,
  margin: '12px auto 20px',
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const BubbleContextCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  marginBottom: theme.spacing(3),
  border: `2px solid ${theme.palette.primary.main}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.dark,
  },
}));

// ───────── Helper Functions ─────────
function getDisplayName(author) {
  if (!author) return "Unknown User";
  if (author.type === "anonymous" || author.display_name === "Anonymous User") {
    return "Anonymous User";
  }
  return author.display_name || "User";
}

function getAvatar(author) {
  return author?.pfphref || undefined;
}

function getDisplayNameComponent(author = {}, isPrivate) {
  const displayName = getDisplayName(author);
  const commonStyle = { fontWeight: 600, fontSize: '0.9rem' };

  if (isPrivate) {
    return (
      <Typography component="span" sx={{ ...commonStyle, color: "text.secondary" }}>
        <PrivateIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, verticalAlign: 'middle' }} />
        {displayName}
      </Typography>
    );
  } else {
    const linkSx = {
      textDecoration: "none",
      color: "primary.main",
      '&:hover': { textDecoration: "underline" }
    };

    if (author.creator_id) {
      return (
        <RouterLink to={`/creators/${author.creator_id}`} style={{ ...commonStyle, ...linkSx }}>
          {displayName}
        </RouterLink>
      );
    } else if (author.user_id) {
      return (
        <RouterLink to={`/user/${author.user_id}`} style={{ ...commonStyle, ...linkSx }}>
          {displayName}
        </RouterLink>
      );
    } else {
      return (
        <Typography component="span" sx={{ ...commonStyle, color: "primary.main" }}>
          {displayName}
        </Typography>
      );
    }
  }
}

function getAvatarComponent(author = {}, isPrivate, size = 36) {
  const avatarSrc = getAvatar(author);
  const displayName = getDisplayName(author);
  const avatarSx = { 
    width: size, 
    height: size,
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.05)',
    }
  };

  if (isPrivate) {
    return <Avatar src={avatarSrc} alt={displayName} sx={{ ...avatarSx, opacity: 0.7 }} />;
  } else {
    const avatarElem = <Avatar src={avatarSrc} alt={displayName} sx={avatarSx} />;

    if (author.creator_id) {
      return (
        <RouterLink to={`/creators/${author.creator_id}`} style={{ textDecoration: 'none' }}>
          {avatarElem}
        </RouterLink>
      );
    } else if (author.user_id) {
      return (
        <RouterLink to={`/user/${author.user_id}`} style={{ textDecoration: 'none' }}>
          {avatarElem}
        </RouterLink>
      );
    } else {
      return avatarElem;
    }
  }
}

// ───────── Reply Component ─────────
function PublicThreadReply({ reply, depth = 0 }) {
  const theme = useTheme();
  const maxDepth = 3;
  const indentSize = Math.min(depth * 20, maxDepth * 20);
  const isExplicitlyPrivate = reply.is_private || reply.author?.type === "anonymous";
  const hasMedia = reply.media_url && reply.media_type;

  return (
    <Box sx={{ ml: `${indentSize}px`, mt: 1.5 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: depth > 0 ? alpha(theme.palette.action.hover, 0.2) : 'background.paper',
          borderLeft: depth > 0 ? `2px solid ${theme.palette.primary.main}` : 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: depth > 0 ? alpha(theme.palette.action.hover, 0.3) : alpha(theme.palette.action.hover, 0.05),
            borderColor: theme.palette.primary.main,
          }
        }}
      >
        <Box display="flex" gap={1.5}>
          {getAvatarComponent(reply.author, isExplicitlyPrivate, 36)}
          <Box flex={1} sx={{ minWidth: 0 }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              {getDisplayNameComponent(reply.author, isExplicitlyPrivate)}
              <Typography variant="caption" color="text.secondary">
                {new Date(reply.created_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                mb: hasMedia ? 1.5 : 1,
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                lineHeight: 1.5
              }}
            >
              {reply.text}
            </Typography>
            {hasMedia && (
              <MediaDisplay
                mediaUrl={reply.media_url}
                mediaType={reply.media_type}
                mediaWidth={reply.media_width}
                mediaHeight={reply.media_height}
                alt={`Media from ${getDisplayName(reply.author)}`}
                maxWidth={400}
                maxHeight={300}
              />
            )}
            {reply.total_reactions > 0 && (
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                  }}
                >
                  <LikeIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
                  <Typography variant="caption" color="error.main" fontWeight={600}>
                    {reply.total_reactions}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// ───────── Enhanced Share Dialog ─────────
function EnhancedShareDialog({ open, onClose, shareData }) {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareData.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareButtons = [
    {
      name: 'Twitter',
      icon: <TwitterIcon />,
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData.title)}&url=${encodeURIComponent(shareData.url)}`
    },
    {
      name: 'Facebook',
      icon: <FacebookIcon />,
      color: '#4267B2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}`
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInIcon />,
      color: '#0077B5',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareData.url)}`
    },
    {
      name: 'WhatsApp',
      icon: <WhatsAppIcon />,
      color: '#25D366',
      url: `https://wa.me/?text=${encodeURIComponent(`${shareData.title} ${shareData.url}`)}`
    },
    {
      name: 'Telegram',
      icon: <TelegramIcon />,
      color: '#0088cc',
      url: `https://t.me/share/url?url=${encodeURIComponent(shareData.url)}&text=${encodeURIComponent(shareData.title)}`
    }
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: theme.shadows[24],
        }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShareIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            Share This Thread
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 1 }}>
          {/* Copy Link Section */}
          <Box
            sx={{
              p: 2,
              mb: 3,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: alpha(theme.palette.action.hover, 0.5),
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              Thread Link
            </Typography>
            <Box display="flex" gap={1}>
              <Typography
                variant="body2"
                sx={{ 
                  flex: 1, 
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                }}
              >
                {shareData.url}
              </Typography>
              <Button
                size="small"
                variant={copied ? "contained" : "outlined"}
                color={copied ? "success" : "primary"}
                startIcon={copied ? <CheckCircleIcon /> : <CopyIcon />}
                onClick={handleCopyLink}
                sx={{ 
                  minWidth: 100,
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </Box>
          </Box>

          {/* Social Media Buttons */}
          <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
            Share on social media
          </Typography>
          <Stack spacing={1.5}>
            {shareButtons.map((platform) => (
              <Button
                key={platform.name}
                variant="outlined"
                fullWidth
                size="large"
                startIcon={platform.icon}
                onClick={() => window.open(platform.url, '_blank', 'width=600,height=400')}
                sx={{
                  justifyContent: 'flex-start',
                  borderColor: alpha(platform.color, 0.3),
                  color: platform.color,
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: platform.color,
                    bgcolor: alpha(platform.color, 0.08),
                  }
                }}
              >
                Share on {platform.name}
              </Button>
            ))}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ───────── Bubble Context Component ─────────
function BubbleContext({ bubbleId, bubbleName, isMobile }) {
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: bubbleData, isLoading } = useQuery(
    ['bubble-info', bubbleId],
    () => API.bubbles.get(bubbleId),
    {
      enabled: !!bubbleId,
      staleTime: 300000, // 5 minutes
    }
  );

  if (!bubbleId || isLoading) {
    return null;
  }

  const bubble = bubbleData?.bubble;
  if (!bubble) return null;

  if (isMobile) {
    return (
      <MobileCard
        onClick={() => navigate(`/bubbles/${bubbleId}`)}
        sx={{ mb: 3, cursor: 'pointer' }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" gap={2}>
            {bubble.avatar_image ? (
              <Avatar 
                src={bubble.avatar_image} 
                sx={{ 
                  width: 56, 
                  height: 56,
                  border: `2px solid ${theme.palette.primary.main}`,
                }} 
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `2px solid ${theme.palette.primary.main}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BubbleIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              </Box>
            )}
            
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Posted in
              </Typography>
              <Typography variant="h6" fontWeight={700} noWrap sx={{ mb: 0.5 }}>
                {bubbleName}
              </Typography>
              <Box display="flex" gap={1} alignItems="center">
                <Chip 
                  size="small" 
                  label={`${bubble.member_count || 0} members`}
                  icon={<PeopleIcon fontSize="small" />}
                  sx={{ height: 22 }}
                />
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                  }}
                >
                  Visit
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </MobileCard>
    );
  }

  return (
    <BubbleContextCard onClick={() => navigate(`/bubbles/${bubbleId}`)}>
      <Box display="flex" alignItems="center" gap={3}>
        {bubble.avatar_image ? (
          <Avatar 
            src={bubble.avatar_image} 
            sx={{ 
              width: 72, 
              height: 72,
              border: `3px solid ${theme.palette.primary.main}`,
              boxShadow: theme.shadows[4],
            }} 
          />
        ) : (
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              border: `3px solid ${theme.palette.primary.main}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: theme.shadows[4],
            }}
          >
            <BubbleIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          </Box>
        )}
        
        <Box flex={1} minWidth={0}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
            Posted in community
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            {bubbleName}
          </Typography>
          {bubble.description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 2,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {bubble.description}
            </Typography>
          )}
          <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
            <Chip 
              size="small" 
              label={`${bubble.member_count || 0} members`}
              icon={<PeopleIcon />}
              sx={{ fontWeight: 600 }}
            />
            {bubble.is_public ? (
              <Chip 
                size="small" 
                label="Public"
                icon={<GlobalIcon />}
                color="success"
                variant="outlined"
              />
            ) : (
              <Chip 
                size="small" 
                label="Private"
                icon={<PrivateIcon />}
                variant="outlined"
              />
            )}
            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon />}
              sx={{ 
                ml: 'auto',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              Visit Community
            </Button>
          </Box>
        </Box>
      </Box>
    </BubbleContextCard>
  );
}

// ───────── Sign Up Prompt Component ─────────
function SignUpPrompt({ isMobile }) {
  const theme = useTheme();
  const navigate = useNavigate();

  if (isMobile) {
    return (
      <MobileCard>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <ThreadIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
            Join the Conversation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
            Create an account to react, reply, and connect with creators on Blitz
          </Typography>
          <Stack spacing={2}>
            <MobileButton
              variant="contained"
              fullWidth
              startIcon={<SignUpIcon />}
              onClick={() => navigate('/onboard')}
            >
              Join Blitz
            </MobileButton>
            <MobileButton
              variant="outlined"
              fullWidth
              startIcon={<LoginIcon />}
              onClick={() => navigate('/login')}
            >
              Sign In
            </MobileButton>
          </Stack>
        </CardContent>
      </MobileCard>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        mb: 3,
        borderRadius: 3,
        border: `2px solid ${theme.palette.primary.main}`,
        bgcolor: alpha(theme.palette.primary.light, 0.05),
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        <ThreadIcon sx={{ fontSize: 42, color: 'primary.main' }} />
      </Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
        Want to join the conversation?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto', lineHeight: 1.7 }}>
        Sign up to react, reply, and connect with creators and brands on Blitz
      </Typography>
      <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
        <Button
          variant="outlined"
          size="large"
          startIcon={<LoginIcon />}
          onClick={() => navigate('/login')}
          sx={{ 
            borderRadius: 3, 
            px: 4,
            py: 1.5,
            fontWeight: 600,
            borderWidth: 1.5,
            '&:hover': {
              borderWidth: 1.5,
            }
          }}
        >
          Sign In
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<SignUpIcon />}
          onClick={() => navigate('/onboard')}
          sx={{ 
            borderRadius: 3, 
            px: 4,
            py: 1.5,
            fontWeight: 600,
            boxShadow: theme.shadows[4],
            '&:hover': {
              boxShadow: theme.shadows[8],
            }
          }}
        >
          Join Blitz
        </Button>
      </Box>
    </Paper>
  );
}

// ───────── Main Component ─────────
export default function PublicThreadPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showShareButton, setShowShareButton] = useState(false);
  const headerRef = useRef(null);

  // Fetch public thread data
  const { data: threadData, isLoading, error } = useQuery(
    ['publicThreadData', postId],
    () => API.zapfeed.getPublicThread(parseInt(postId)),
    {
      enabled: !!postId,
      staleTime: 60000,
      retry: 1
    }
  );

  // Scroll handler
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);

      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setShowShareButton(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Set page title and meta tags
  useEffect(() => {
    if (threadData?.share_metadata) {
      document.title = threadData.share_metadata.title;

      const updateMetaTag = (name, content) => {
        let tag = document.querySelector(`meta[name="${name}"]`) ||
          document.querySelector(`meta[property="${name}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      updateMetaTag('description', threadData.share_metadata.description);
      updateMetaTag('og:title', threadData.share_metadata.title);
      updateMetaTag('og:description', threadData.share_metadata.description);
      updateMetaTag('og:url', threadData.share_metadata.url);
      updateMetaTag('og:image', threadData.share_metadata.image);
      updateMetaTag('og:type', 'article');
      updateMetaTag('twitter:card', 'summary_large_image');
      updateMetaTag('twitter:title', threadData.share_metadata.title);
      updateMetaTag('twitter:description', threadData.share_metadata.description);
      updateMetaTag('twitter:image', threadData.share_metadata.image);
    }
  }, [threadData]);

  const handleShare = () => {
    if (navigator.share && threadData?.share_metadata && isMobile) {
      navigator.share({
        title: threadData.share_metadata.title,
        text: threadData.share_metadata.description,
        url: threadData.share_metadata.url,
      }).catch(() => {
        setShareSheetOpen(true);
      });
    } else if (isMobile) {
      setShareSheetOpen(true);
    } else {
      setShareDialogOpen(true);
    }
  };

  const handleCopyLink = async () => {
    if (threadData?.share_metadata) {
      try {
        await navigator.clipboard.writeText(threadData.share_metadata.url);
        // Could add a snackbar notification here
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={150} height={24} />
        </Box>
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Box display="flex" gap={2}>
            <Skeleton variant="circular" width={56} height={56} />
            <Box flex={1}>
              <Skeleton variant="text" width="30%" height={24} />
              <Skeleton variant="text" width="100%" height={80} />
            </Box>
          </Box>
        </Paper>
        {Array.from({ length: 2 }).map((_, i) => (
          <Paper key={i} sx={{ p: 2, mb: 2, ml: 2, borderRadius: 3 }}>
            <Box display="flex" gap={2}>
              <Skeleton variant="circular" width={36} height={36} />
              <Box flex={1}>
                <Skeleton variant="text" width="25%" height={20} />
                <Skeleton variant="text" width="80%" height={50} />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
          {error.response?.status === 404
            ? "Thread not found or not available for public viewing."
            : "Failed to load thread. Please try again."
          }
        </Alert>
        <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!threadData?.root_post) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 3 }}>
          Thread not found.
        </Alert>
        <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  const rootPost = threadData.root_post;
  const repliesTree = threadData.replies_tree || {};
  const directReplies = repliesTree[rootPost.id] || [];
  const rootIsPrivate = rootPost.is_private || rootPost.author?.type === "anonymous";
  const rootHasMedia = rootPost.media_url && rootPost.media_type;
  const totalReactions = rootPost.total_reactions || 0;
  const replyCount = directReplies.length;

  // Extract bubble information
  const bubbleId = rootPost.bubble_id;
  const bubbleName = rootPost.bubble_name;

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  MOBILE RENDER                                                           */
  /* ───────────────────────────────────────────────────────────────────────── */
  if (isMobile) {
    return (
      <>
        {/* Mobile App Bar */}
        <MobileAppBar position="fixed" scrolled={scrolled} elevation={0}>
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => navigate(-1)}
              sx={{ color: scrolled ? 'text.primary' : 'white', mr: 2 }}
            >
              <BackIcon />
            </IconButton>
            <Collapse in={scrolled} orientation="horizontal">
              <Stack direction="row" spacing={1} alignItems="center">
                <ThreadIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography 
                  variant="subtitle1" 
                  fontWeight={700}
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: 180, sm: 280 },
                  }}
                >
                  Thread
                </Typography>
              </Stack>
            </Collapse>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton
              onClick={handleShare}
              sx={{ color: scrolled ? 'text.primary' : 'white' }}
            >
              <ShareIcon />
            </IconButton>
          </Toolbar>
        </MobileAppBar>

        {/* Hero Section */}
        <Box ref={headerRef}>
          <ModernGradientHeader>
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, pt: 10, pb: 4, px: 3 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip
                  icon={<ThreadIcon sx={{ fontSize: 14 }} />}
                  label="Public Thread"
                  size="small"
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    color: 'white',
                    fontWeight: 700,
                    height: 28,
                    fontSize: '0.75rem',
                    backdropFilter: 'blur(10px)',
                  }}
                />
                {bubbleName && (
                  <Chip
                    icon={<BubbleIcon sx={{ fontSize: 14 }} />}
                    label={bubbleName}
                    size="small"
                    onClick={() => bubbleId && navigate(`/bubbles/${bubbleId}`)}
                    sx={{
                      bgcolor: alpha('#fff', 0.2),
                      color: 'white',
                      fontWeight: 700,
                      height: 28,
                      fontSize: '0.75rem',
                      backdropFilter: 'blur(10px)',
                      cursor: bubbleId ? 'pointer' : 'default',
                      '&:hover': bubbleId ? {
                        bgcolor: alpha('#fff', 0.3),
                      } : {},
                    }}
                  />
                )}
              </Stack>
              
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "white",
                  fontSize: "1.75rem",
                  lineHeight: 1.2,
                  mb: 3,
                  textShadow: "0 2px 12px rgba(0,0,0,0.5)",
                }}
              >
                {rootPost.post_type === 'achievement' && rootPost.achievement 
                  ? rootPost.achievement.blitzbolt_title 
                  : "Discussion Thread"}
              </Typography>

              {/* Stats */}
              <Stack direction="row" spacing={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: alpha('#fff', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <LikeIcon sx={{ fontSize: 18, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                    {totalReactions}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: alpha('#fff', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <ReplyIcon sx={{ fontSize: 18, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                    {replyCount}
                  </Typography>
                </Box>
              </Stack>
            </Container>
          </ModernGradientHeader>
        </Box>

        {/* Content */}
        <Box sx={{ bgcolor: "background.default", pb: APPSHELL_HEIGHT + BOTTOM_PADDING + 20 }}>
          <Container maxWidth="lg" sx={{ px: 2, pt: 3 }}>
            {/* Bubble Context */}
            {bubbleId && bubbleName && (
              <BubbleContext 
                bubbleId={bubbleId} 
                bubbleName={bubbleName}
                isMobile={isMobile}
              />
            )}

            {/* Root Post */}
            <MobileCard>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" gap={2} mb={2}>
                  {getAvatarComponent(rootPost.author, rootIsPrivate, 48)}
                  <Box flex={1} sx={{ minWidth: 0 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      {getDisplayNameComponent(rootPost.author, rootIsPrivate)}
                      {rootPost.post_type === 'achievement' && (
                        <Chip
                          icon={<AchievementIcon sx={{ fontSize: 14 }} />}
                          label="Achievement"
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.warning.main, 0.15),
                            color: theme.palette.warning.dark,
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(rootPost.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                </Box>

                {/* Content */}
                {rootPost.post_type === 'achievement' && rootPost.achievement ? (
                  <Box>
                    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.6, fontWeight: 500 }}>
                      {rootPost.achievement.blitzbolt_description}
                    </Typography>
                    <Chip
                      label={`+${rootPost.achievement.points_earned} points`}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.success.main, 0.15),
                        color: theme.palette.success.main,
                        fontWeight: 700,
                        fontSize: '0.8rem',
                      }}
                    />
                  </Box>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      lineHeight: 1.6,
                    }}
                  >
                    {rootPost.text}
                  </Typography>
                )}

                {rootHasMedia && (
                  <Box sx={{ mt: 2 }}>
                    <MediaDisplay
                      mediaUrl={rootPost.media_url}
                      mediaType={rootPost.media_type}
                      mediaWidth={rootPost.media_width}
                      mediaHeight={rootPost.media_height}
                      alt={`Media from ${getDisplayName(rootPost.author)}`}
                      maxWidth="100%"
                      maxHeight={400}
                    />
                  </Box>
                )}

                {/* Engagement Stats */}
                {(totalReactions > 0 || replyCount > 0) && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Stack direction="row" spacing={3}>
                      {totalReactions > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LikeIcon sx={{ fontSize: 20, color: 'error.main' }} />
                          <Typography variant="body2" fontWeight={600}>
                            {totalReactions} {totalReactions === 1 ? 'like' : 'likes'}
                          </Typography>
                        </Box>
                      )}
                      {replyCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ReplyIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          <Typography variant="body2" fontWeight={600}>
                            {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </MobileCard>

            {/* Sign Up Prompt */}
            <SignUpPrompt isMobile={isMobile} />

            {/* Replies */}
            {replyCount > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, px: 1, fontWeight: 700 }}>
                  Replies
                </Typography>
                {directReplies.map((reply) => (
                  <PublicThreadReply key={reply.id} reply={reply} depth={0} />
                ))}
              </Box>
            )}

            {replyCount === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ReplyIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No replies yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Be the first to join the conversation!
                </Typography>
              </Box>
            )}
          </Container>
        </Box>

        {/* Sticky Share Button */}
        {showShareButton && (
          <StickyShareButton
            variant="extended"
            onClick={handleShare}
          >
            <ShareIcon sx={{ mr: 1 }} />
            Share Thread
          </StickyShareButton>
        )}

        {/* Share Bottom Sheet */}
        <BottomSheet
          anchor="bottom"
          open={shareSheetOpen}
          onClose={() => setShareSheetOpen(false)}
          onOpen={() => setShareSheetOpen(true)}
          disableSwipeToOpen
        >
          <Box sx={{ p: 3 }}>
            <BottomSheetHandle />
            
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ShareIcon sx={{ color: 'primary.main', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Share This Thread
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <MobileButton
                onClick={handleCopyLink}
                variant="contained"
                fullWidth
                startIcon={<CopyIcon />}
              >
                Copy Link
              </MobileButton>
              
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Or share via
                </Typography>
              </Divider>

              <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
                <IconButton
                  onClick={() => {
                    if (threadData?.share_metadata) {
                      window.open(
                        `https://twitter.com/intent/tweet?text=${encodeURIComponent(threadData.share_metadata.title)}&url=${encodeURIComponent(threadData.share_metadata.url)}`,
                        '_blank',
                        'width=600,height=400'
                      );
                    }
                  }}
                  sx={{
                    bgcolor: alpha('#1DA1F2', 0.1),
                    color: '#1DA1F2',
                    '&:hover': { bgcolor: alpha('#1DA1F2', 0.2) }
                  }}
                >
                  <TwitterIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    if (threadData?.share_metadata) {
                      window.open(
                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(threadData.share_metadata.url)}`,
                        '_blank',
                        'width=600,height=400'
                      );
                    }
                  }}
                  sx={{
                    bgcolor: alpha('#4267B2', 0.1),
                    color: '#4267B2',
                    '&:hover': { bgcolor: alpha('#4267B2', 0.2) }
                  }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    if (threadData?.share_metadata) {
                      window.open(
                        `https://wa.me/?text=${encodeURIComponent(`${threadData.share_metadata.title} ${threadData.share_metadata.url}`)}`,
                        '_blank'
                      );
                    }
                  }}
                  sx={{
                    bgcolor: alpha('#25D366', 0.1),
                    color: '#25D366',
                    '&:hover': { bgcolor: alpha('#25D366', 0.2) }
                  }}
                >
                  <WhatsAppIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    if (threadData?.share_metadata) {
                      window.open(
                        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(threadData.share_metadata.url)}`,
                        '_blank',
                        'width=600,height=400'
                      );
                    }
                  }}
                  sx={{
                    bgcolor: alpha('#0077B5', 0.1),
                    color: '#0077B5',
                    '&:hover': { bgcolor: alpha('#0077B5', 0.2) }
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </BottomSheet>
      </>
    );
  }

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  DESKTOP RENDER                                                          */
  /* ───────────────────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <ModernGradientHeader>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
          <Box sx={{ py: 5 }}>
            {/* Back Button & Share */}
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Button
                startIcon={<BackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.1),
                  }
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                startIcon={<ShareIcon />}
                onClick={handleShare}
                sx={{
                  bgcolor: alpha('#fff', 0.2),
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.3),
                  }
                }}
              >
                Share Thread
              </Button>
            </Box>

            {/* Badges */}
            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap">
              <Chip
                icon={<ThreadIcon />}
                label="Public Thread"
                sx={{
                  bgcolor: alpha('#fff', 0.2),
                  color: 'white',
                  fontWeight: 700,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha('#fff', 0.3)}`,
                }}
              />
              {bubbleName ? (
                <Chip
                  icon={<BubbleIcon />}
                  label={bubbleName}
                  onClick={() => bubbleId && navigate(`/bubbles/${bubbleId}`)}
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    color: 'white',
                    fontWeight: 700,
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha('#fff', 0.3)}`,
                    cursor: bubbleId ? 'pointer' : 'default',
                    '&:hover': bubbleId ? {
                      bgcolor: alpha('#fff', 0.3),
                    } : {},
                  }}
                />
              ) : (
                <Chip
                  icon={<GlobalIcon />}
                  label="Global"
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    color: 'white',
                    fontWeight: 700,
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha('#fff', 0.3)}`,
                  }}
                />
              )}
            </Stack>

            {/* Title */}
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: "white",
                fontSize: "2.5rem",
                lineHeight: 1.2,
                mb: 4,
                textShadow: "0 4px 20px rgba(0,0,0,0.5)",
                maxWidth: 800,
              }}
            >
              {rootPost.post_type === 'achievement' && rootPost.achievement 
                ? rootPost.achievement.blitzbolt_title 
                : "Discussion Thread"}
            </Typography>

            {/* Stats */}
            <Stack direction="row" spacing={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: alpha('#fff', 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <LikeIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), display: 'block', fontWeight: 600 }}>
                    Reactions
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    {totalReactions}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: alpha('#fff', 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <ReplyIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), display: 'block', fontWeight: 600 }}>
                    Replies
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    {replyCount}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Container>
      </ModernGradientHeader>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ mt: -4, position: 'relative', zIndex: 2, pb: 8 }}>
        {/* Bubble Context */}
        {bubbleId && bubbleName && (
          <BubbleContext 
            bubbleId={bubbleId} 
            bubbleName={bubbleName}
            isMobile={isMobile}
          />
        )}

        {/* Root Post */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 4,
            border: `2px solid ${theme.palette.primary.main}`,
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[8],
          }}
        >
          <Box display="flex" gap={3} mb={3}>
            {getAvatarComponent(rootPost.author, rootIsPrivate, 64)}
            <Box flex={1} sx={{ minWidth: 0 }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                {getDisplayNameComponent(rootPost.author, rootIsPrivate)}
                {rootPost.post_type === 'achievement' && (
                  <Chip
                    icon={<AchievementIcon />}
                    label="Achievement"
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                      color: theme.palette.warning.dark,
                      height: 26,
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {new Date(rootPost.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          {rootPost.post_type === 'achievement' && rootPost.achievement ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, fontSize: '1.05rem' }}>
                {rootPost.achievement.blitzbolt_description}
              </Typography>
              <Chip
                label={`+${rootPost.achievement.points_earned} points`}
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  color: theme.palette.success.main,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  height: 32,
                }}
              />
            </Box>
          ) : (
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                lineHeight: 1.7,
                fontSize: '1.05rem',
              }}
            >
              {rootPost.text}
            </Typography>
          )}

          {rootHasMedia && (
            <Box sx={{ mt: 3 }}>
              <MediaDisplay
                mediaUrl={rootPost.media_url}
                mediaType={rootPost.media_type}
                mediaWidth={rootPost.media_width}
                mediaHeight={rootPost.media_height}
                alt={`Media from ${getDisplayName(rootPost.author)}`}
                maxWidth="100%"
                maxHeight={500}
              />
            </Box>
          )}

          {/* Engagement Stats */}
          {(totalReactions > 0 || replyCount > 0) && (
            <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Stack direction="row" spacing={4}>
                {totalReactions > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.error.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LikeIcon sx={{ fontSize: 20, color: 'error.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {totalReactions}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {totalReactions === 1 ? 'like' : 'likes'}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {replyCount > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ReplyIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {replyCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {replyCount === 1 ? 'reply' : 'replies'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Sign Up Prompt */}
        <SignUpPrompt isMobile={isMobile} />

        {/* Replies Section */}
        {replyCount > 0 && (
          <Box>
            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
              <Typography variant="h5" fontWeight={700}>
                Replies
              </Typography>
              <Chip 
                label={replyCount}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  fontWeight: 700,
                }}
              />
            </Box>
            {directReplies.map((reply) => (
              <PublicThreadReply key={reply.id} reply={reply} depth={0} />
            ))}
          </Box>
        )}

        {replyCount === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.action.hover, 0.5),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ReplyIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            </Box>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
              No replies yet
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Be the first to join the conversation!
            </Typography>
          </Box>
        )}
      </Container>

      {/* Share Dialog */}
      {threadData?.share_metadata && (
        <EnhancedShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          shareData={threadData.share_metadata}
        />
      )}
    </Box>
  );
}